import { CalendarDays, Headset, MessageSquare, UserCheck, Users, Wallet } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Kpi, Section } from '@/components/common/Blocks'
import { PageHeader } from '@/components/common/PageHeader'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/select'
import { fmtMoney } from '../shared/format'
import { useCrmOverview, useMeetings, useRequests } from './api'
import { MeetingsTable } from './components/MeetingsTable'
import { RequestList } from './components/RecordLists'
import { FollowUpsCard, FunnelCard } from './components/SideCards'
import { useCrm } from './ui'

export function CrmOverviewPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projects, customerById } = useCrm()
  const [projectId, setProjectId] = useState('all')
  const overview = useCrmOverview(projectId)
  const meetings = useMeetings()
  const requests = useRequests()
  const o = overview.data
  const inProject = (customerId: string) => projectId === 'all' || customerById.get(customerId)?.projectId === projectId
  const recent = (meetings.data ?? []).filter((m) => inProject(m.customerId)).slice(0, 6)
  const openReq = (requests.data ?? []).filter((r) => r.status !== 'resolved' && inProject(r.customerId)).slice(0, 5)

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.customers')}
        title={t('crm.overview.title')}
        description={t('crm.overview.subtitle')}
        actions={
          <Select
            className="w-48"
            value={projectId}
            onChange={setProjectId}
            aria-label={t('crm.fields.project')}
            options={[{ value: 'all', label: t('crm.allProjects') }, ...projects.map((p) => ({ value: p.id, label: p.name }))]}
          />
        }
      />
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-5">
          <Kpi icon={Users} tone="primary" label={t('crm.stage.lead')} value={o?.counts.lead ?? '—'} />
          <Kpi icon={CalendarDays} tone="info" label={t('crm.kpi.demo')} value={o?.counts.demo ?? '—'} />
          <Kpi icon={MessageSquare} tone="warning" label={t('crm.stage.proposal')} value={o?.counts.proposal ?? '—'} />
          <Kpi icon={UserCheck} tone="success" label={t('crm.kpi.active')} value={o?.counts.customer ?? '—'} />
          <Kpi icon={Wallet} tone="danger" label={t('crm.kpi.pending')} value={o ? fmtMoney(o.pendingCollection) : '—'} />
        </div>
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="min-w-0 space-y-4">
            <Section
              title={t('crm.overview.recentMeetings')}
              icon={MessageSquare}
              bodyClassName="p-0"
              actions={
                <Button size="sm" variant="link" className="h-auto p-0" onClick={() => navigate('/customers/meetings')}>
                  {t('crm.seeAll')}
                </Button>
              }
            >
              <MeetingsTable rows={recent} showCustomer onSelect={(m) => navigate(`/customers/${m.customerId}/meetings?m=${m.id}`)} />
            </Section>
            <Section
              title={t('crm.overview.openRequests')}
              icon={Headset}
              bodyClassName="p-0"
              actions={
                <Button size="sm" variant="link" className="h-auto p-0" onClick={() => navigate('/customers/support')}>
                  {t('crm.seeAll')}
                </Button>
              }
            >
              <RequestList rows={openReq} showCustomer canWrite={false} onEdit={() => undefined} onDelete={() => undefined} />
            </Section>
          </div>
          <aside className="space-y-4">
            <FunnelCard overview={o} title={t('crm.funnelTitle', { project: projectId === 'all' ? t('crm.allProjects') : projects.find((p) => p.id === projectId)?.name ?? '' })} />
            <FollowUpsCard overview={o} />
          </aside>
        </div>
      </div>
    </>
  )
}
